/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';

import {
    Button,
    DividerNew,
    FormValidationAlert,
    Heading,
    Label,
    type FormValidationAlertErrors,
    type VerificationCodeFieldRef,
    VerificationCodeField,
} from 'sarsaparilla';
import { getMessageFromError } from 'sarsaparilla/src/utils';

import {
    useRecaptchaWithFallback,
    recaptchaV2ValidationError,
    RecaptchaCheckbox,
} from '../../../recaptcha';

import {
    getAccountRegistrationPayload,
    postAccountRegistration,
    postAccountRegistrationErrors,
    putAccountEmailValidation,
    putAccountEmailValidationErrors,
} from '../../../api/account';

import { ProtectedByRecaptcha } from '../components/ProtectedByRecaptcha';

import { VerifyEmailErrors, type VerifyEmailErrorType } from './VerifyEmailErrors';

import styles from './VerifyEmailForm.module.scss';

interface VerifyEmailFormProps {
    emailAddress: string;
    onEmailVerified: () => void;
}

export function VerifyEmailForm({ emailAddress, onEmailVerified }: VerifyEmailFormProps) {
    const [isLoading, setIsLoading] = React.useState(false);
    const [isResendingEmail, setIsResendingEmail] = React.useState(false);
    const [errorType, setErrorType] = React.useState<VerifyEmailErrorType>(null);
    const [validationErrors, setValidationErrors] =
        React.useState<FormValidationAlertErrors | null>(null);
    const [showResendMessage, setShowResendMessage] = React.useState(false);

    const resendTimeoutRef = React.useRef<number | null>(null);
    const verificationCodeRef = React.useRef<VerificationCodeFieldRef>(null);
    const resendButtonRef = React.useRef<HTMLButtonElement | null>(null);

    const {
        getToken,
        getRecaptchaRegion,
        handleRecaptchaError,
        isV2Invalid,
        recaptchaCheckboxProps,
    } = useRecaptchaWithFallback();

    React.useEffect(() => {
        return () => {
            if (resendTimeoutRef.current) {
                window.clearTimeout(resendTimeoutRef.current);
            }
        };
    }, []);

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLoading(true);
        setIsResendingEmail(false);
        setErrorType(null);

        // Get form values and validate
        const formData = new FormData(event.currentTarget);
        const code = formData.get('code')?.toString().trim();

        const validation = verificationCodeRef.current?.validate();

        if (!code || validation?.isInvalid) {
            verificationCodeRef.current?.focus();
            setIsLoading(false);
            return;
        }

        try {
            const { data } = await putAccountEmailValidation(code);

            if (data.token) {
                // Go to next step
                onEmailVerified();
            } else {
                setErrorType('unknown');
            }
        } catch (error) {
            const messageFromError = getMessageFromError(error);

            if (messageFromError === putAccountEmailValidationErrors.INVALID_CODE) {
                setErrorType('invalidCode');
            } else {
                setErrorType('unknown');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const onResendEmail = async () => {
        setIsLoading(false);
        setIsResendingEmail(true);
        setErrorType(null);
        setValidationErrors(null);

        if (resendTimeoutRef.current) {
            window.clearTimeout(resendTimeoutRef.current);
        }

        // Validate recaptcha
        if (isV2Invalid) {
            setValidationErrors([recaptchaV2ValidationError]);
            setIsResendingEmail(false);
            return;
        }

        try {
            // Get Recaptcha type and token
            const recaptchaToken = await getToken('signup');
            const recaptchaRegion = getRecaptchaRegion();

            // Extra check to make sure we have a valid token
            if (!recaptchaToken) {
                setErrorType('recaptchaFailed');
                setIsResendingEmail(false);
                return;
            }

            // Resending the email is actually just resubmitting the API call from the
            // previous step, so we grab the payload from session storage that is automatically
            // stored when postAccountRegistration is called and add in fresh recaptcha data
            const storedPayloadItems = getAccountRegistrationPayload();

            // Verify that we have a payload to resubmit
            if (!storedPayloadItems) {
                setErrorType('unknown');
                setIsResendingEmail(false);
                return;
            }

            // Resubmit the API call with fresh recaptcha data
            const { data } = await postAccountRegistration({
                ...storedPayloadItems,
                recaptchaToken,
                recaptchaRegion,
            });

            const isSuccess = data.success;

            if (!isSuccess) {
                setErrorType('unknownEmail');
            }

            setShowResendMessage(true);
        } catch (error) {
            // Check for recaptcha-specific errors
            const { shouldRetry, isFailed } = handleRecaptchaError(error);
            const errorMessage = getMessageFromError(error);

            if (shouldRetry) {
                setErrorType('additionalChallenge');
            } else if (isFailed) {
                setErrorType('recaptchaFailed');
            } else if (errorMessage.includes(postAccountRegistrationErrors.LOCKOUT)) {
                setErrorType('lockout');
            } else {
                setShowResendMessage(true);
                setErrorType('unknownEmail');
            }
        } finally {
            setIsResendingEmail(false);
            resendTimeoutRef.current = window.setTimeout(() => {
                setShowResendMessage(false);
            }, 3000);
        }
    };

    const resendEmailButtonText = 'Resend Email';

    return (
        <div className="px-sm-3 pt-sm-3 pb-sm-1">
            <Heading headingLevel={1} appearance="h4" id="modal-heading">
                Check Your Email
            </Heading>

            <div className="text-md mt-2 mt-4">
                <p className="heading-xs">
                    We sent a verification code to{' '}
                    <span className={styles.emailAddress}>{emailAddress}</span>.
                </p>

                <p className="text-md mt-1">
                    Please enter the code to confirm your account. If you do not receive
                    an email, please check your spam folder or select{' '}
                    <strong className="font-weight-semibold">
                        {resendEmailButtonText}
                    </strong>{' '}
                    below.
                </p>
            </div>

            <form onSubmit={onSubmit} className="mt-4">
                <FormValidationAlert errors={validationErrors} className="mb-4" />

                <VerifyEmailErrors errorType={errorType} className="mb-4" />

                <Label
                    htmlFor="verification-code"
                    isRequired
                    className="mb-1 text-center"
                >
                    Verification Code
                </Label>
                <VerificationCodeField
                    label="Verification Code"
                    id="verification-code"
                    name="code"
                    isRequired
                    className="mx-auto"
                    ref={verificationCodeRef}
                />

                <RecaptchaCheckbox
                    {...recaptchaCheckboxProps}
                    isFallbackOnly
                    action="signup"
                    className="mt-3"
                />

                <Button
                    type="submit"
                    size="lg"
                    shouldFitContainer
                    className="mt-5"
                    isLoading={isLoading}
                >
                    Verify Email Code
                </Button>

                <Button
                    type="button"
                    shouldFitContainer
                    className="mt-1"
                    appearance="subtle"
                    onClick={onResendEmail}
                    isLoading={isResendingEmail}
                    isDisabled={isLoading || showResendMessage}
                    innerRef={resendButtonRef}
                >
                    {resendEmailButtonText}
                </Button>
            </form>

            <div className={styles.popupContainer} aria-live="polite">
                <div className={styles.popupContentWrapper}>
                    <div
                        className={cx(styles.popupContent, {
                            [styles.visible]: showResendMessage,
                            [styles.error]: errorType === 'unknownEmail',
                        })}
                        aria-hidden={!showResendMessage}
                    >
                        {errorType === 'unknownEmail'
                            ? 'Failed to send email. Try again in a moment.'
                            : 'Email successfully sent'}
                    </div>
                </div>
            </div>

            <DividerNew className="mb-2" />

            <ProtectedByRecaptcha />
        </div>
    );
}
